import { c as _c } from "react/compiler-runtime";
import { type CSSObject } from '@precis-digital/kurama/theme';
import { TableFooterCell as KuramaTableFooterCell } from '@precis-digital/kurama/Table';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { type ColumnPinningPosition, flexRender } from '@tanstack/react-table';
import type { ReactElement } from 'react';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import type { ElementsDescriptionsMap, TotalsAndRangeData } from '../../../internalTypes';
import type { ChartCardTFunction, ChartDataRow } from '../../../types';
import TableFooterCellContent from './TableFooterCellContent';
import { getColumnCellStyle, getColumnDataKey } from '../utils';
import type { ChartCardTableFooterContextResolved, ChartCardTableHeader } from '../tanstack';
interface TableFooterCellProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  header: ChartCardTableHeader<DataKey, DataRow>;
  pinned: ColumnPinningPosition;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  allowColumnResizing: boolean;
  totalsData: TotalsAndRangeData<DataKey>['totals'];
  preferTotalsData?: boolean;
  rowHovered: boolean;
  t: ChartCardTFunction;
}
const TableFooterCell = t0 => {
  const $ = _c(19);
  const {
    header,
    pinned,
    elementsDescriptionsMap,
    allowColumnResizing,
    totalsData,
    preferTotalsData: t1,
    rowHovered,
    t
  } = t0;
  const preferTotalsData = t1 === undefined ? false : t1;
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  const {
    id: headerId,
    isPlaceholder,
    getContext,
    column
  } = header;
  let cellContent = null;
  if (!isPlaceholder) {
    let t2;
    if ($[0] !== elementsDescriptionsMap || $[1] !== getContext || $[2] !== preferTotalsData || $[3] !== t || $[4] !== totalsData) {
      const rawContext = getContext();
      const dataKey = getColumnDataKey(rawContext.column);
      const context = {
        ...rawContext,
        meta: {
          chartCard: {
            elementsDescriptionsMap,
            total: dataKey == null ? undefined : totalsData[dataKey],
            preferTotalsData,
            t
          }
        }
      };
      t2 = flexRender(TableFooterCellContent, context);
      $[0] = elementsDescriptionsMap;
      $[1] = getContext;
      $[2] = preferTotalsData;
      $[3] = t;
      $[4] = totalsData;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    cellContent = t2;
  }
  let t2;
  if ($[6] !== allowColumnResizing || $[7] !== column || $[8] !== defaults || $[9] !== pinned || $[10] !== rowHovered || $[11] !== theme) {
    t2 = getColumnCellStyle({
      defaults,
      theme,
      column,
      allowResizing: allowColumnResizing,
      pinned,
      type: "footer",
      rowHovered
    });
    $[6] = allowColumnResizing;
    $[7] = column;
    $[8] = defaults;
    $[9] = pinned;
    $[10] = rowHovered;
    $[11] = theme;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const cellStyle = t2;
  const t3 = `calc(var(--header-${headerId}-size) * 1px)`;
  let t4;
  if ($[13] !== allowColumnResizing || $[14] !== cellContent || $[15] !== cellStyle || $[16] !== headerId || $[17] !== t3) {
    t4 = <StyledTableFooterCell key={headerId} allowResizing={allowColumnResizing} width={t3} cellStyle={cellStyle}>{cellContent}</StyledTableFooterCell>;
    $[13] = allowColumnResizing;
    $[14] = cellContent;
    $[15] = cellStyle;
    $[16] = headerId;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
const StyledTableFooterCell = styled(KuramaTableFooterCell, {
  shouldForwardProp: prop => prop !== 'width' && prop !== 'cellStyle' && prop !== 'allowResizing'
})<{
  allowResizing: boolean;
  cellStyle: CSSObject;
  width?: number | string;
}>(({
  theme,
  allowResizing,
  width,
  cellStyle
}): CSSObject => ({
  display: 'flex',
  width,
  alignItems: 'center',
  padding: allowResizing ? `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(1)}` : `${theme.spacing(1)}`,
  minHeight: theme.spacing(8),
  ...cellStyle
}));
export default TableFooterCell;